import {
  Filters,
  Filter,
  FilterCategory,
  filterOptionToLabelMap,
  FilterOptions,
  FilterOption,
} from '../content/ContentFilters';
import {
  ApproverType,
  ContentType,
  Publication,
  PublisherReviewStatus,
  RType,
  Workload,
  TechDomainDisplayNames,
} from '@amzn/awscat-pattern-library-service-model';
import {
  awsServiceReadonlyDisplayNames,
  barRaiserReviewStatusDisplayNames,
  BR_APPROVED_VALUE,
  BR_APPROVAL_PENDING_VALUE,
  contentRequestStateDisplayNames,
  contentTypeDisplayNames,
  environmentDisplayNames,
  publicationStateDisplayNames,
  rTypeDisplayNames,
  workloadDisplayNames,
} from '../utils/DisplayNames';
import { publicationStateVisibilityValues } from '../utils/SelectableOptions';
import { workflowStateToVisibilityMap } from '../utils/DisplayNames';
import arraySort from 'array-sort';

export const isBRApproved = (publication: Publication) => {
  const reviewInfo = publication?.publisherReviewInfo;
  if (!reviewInfo) {
    return false;
  }
  return (
    reviewInfo.reviewStatus === PublisherReviewStatus.APPROVED &&
    reviewInfo.approverType === ApproverType.BAR_RAISER
  );
};

const publicationsFilterOptionToMetadataMap = new Map<string, string>([
  [FilterCategory.ContentType, 'contentMetadata'],
  [FilterCategory.Code, ''],
  [FilterCategory.TechDomains, 'templateMetadata'],
  [FilterCategory.RType, 'templateMetadata'],
  [FilterCategory.Workload, 'templateMetadata'],
  [FilterCategory.AwsServices, 'templateMetadata'],
  [FilterCategory.Environment, 'templateMetadata'],
  [FilterCategory.PublicationState, ''],
  [FilterCategory.IsFavoriteCollection, ''],
  [FilterCategory.BarRaiserReviewStatus, ''],
]);

const pubicationsFilterOptionToValuesMap = new Map<string, string[]>([
  [FilterCategory.ContentType, ['contentType']],
  [FilterCategory.Code, ['code']],
  [FilterCategory.TechDomains, ['mainTechDomain', 'techDomains', 'technologies']],
  [FilterCategory.RType, ['rType']],
  [FilterCategory.Workload, ['workload']],
  [FilterCategory.AwsServices, ['awsServices']],
  [FilterCategory.Environment, ['environment']],
  [FilterCategory.PublicationState, ['publicationState']],
  [FilterCategory.IsFavoriteCollection, ['hasRated']],
  [FilterCategory.BarRaiserReviewStatus, ['barRaiserReviewStatus']],
]);

export interface FilterOptionToApply {
  metadata: string;
  options: FilterOption[];
  values: string[];
  mappedValues?: Map<string, string>;
}

export const getFiltersToApply = (filterOptions: Filters): FilterOptionToApply[] => {
  return Object.values(filterOptions).reduce<FilterOptionToApply[]>((validFilters, filter) => {
    const validOptions = filter
      ? Object.values(filter.options).filter((option) => option.selected)
      : [];
    if (validOptions.length) {
      validFilters.push({
        metadata: filter.metadata,
        options: validOptions,
        values: filter.values,
        mappedValues: filter.mappedValues,
      });
    }
    return validFilters;
  }, []);
};

export const getPublicationInitialFilterOptions = (
  hideFilterCategory: FilterCategory[] = [],
  earlySharing = false,
) => {
  const initialFilterOptions: Filters = {
    // @ts-expect-error TS2322 - strict migration
    barRaiserReviewStatus: createPublicationFilterOption(
      FilterCategory.BarRaiserReviewStatus,
      barRaiserReviewStatusDisplayNames,
      hideFilterCategory,
    ),
    // @ts-expect-error TS2322 - strict migration
    contentType: createPublicationFilterOption(
      FilterCategory.ContentType,
      contentTypeDisplayNames,
      hideFilterCategory,
    ),
    // @ts-expect-error TS2322 - strict migration
    code: createPublicationFilterOption(FilterCategory.Code, null, hideFilterCategory),
    // @ts-expect-error TS2322 - strict migration
    techDomains: createPublicationFilterOption(
      FilterCategory.TechDomains,
      TechDomainDisplayNames,
      hideFilterCategory,
    ),
    // tslint:disable-next-line: object-literal-sort-keys
    // @ts-expect-error TS2322 - strict migration
    rType: createPublicationFilterOption(
      FilterCategory.RType,
      rTypeDisplayNames,
      hideFilterCategory,
    ),
    // @ts-expect-error TS2322 - strict migration
    workload: createPublicationFilterOption(
      FilterCategory.Workload,
      workloadDisplayNames,
      hideFilterCategory,
    ),
    // @ts-expect-error TS2322 - strict migration
    awsServices: createPublicationFilterOption(
      FilterCategory.AwsServices,
      awsServiceReadonlyDisplayNames,
      hideFilterCategory,
    ),
    // @ts-expect-error TS2322 - strict migration
    environment: createPublicationFilterOption(
      FilterCategory.Environment,
      environmentDisplayNames,
      hideFilterCategory,
    ),
    // @ts-expect-error TS2322 - strict migration
    authors: createPublicationFilterOption(FilterCategory.Authors, null, hideFilterCategory),
    // @ts-expect-error TS2322 - strict migration
    state: createPublicationFilterOption(
      FilterCategory.State,
      contentRequestStateDisplayNames,
      hideFilterCategory,
    ),
    // @ts-expect-error TS2322 - strict migration
    publicationState: createPublicationFilterOption(
      FilterCategory.PublicationState,
      // @ts-expect-error TS2345 - strict migration
      earlySharing ? null : publicationStateDisplayNames,
      hideFilterCategory,
      earlySharing ? workflowStateToVisibilityMap : publicationStateVisibilityValues,
    ),
  };
  return initialFilterOptions;
};

export const createPublicationFilterOption = (
  filterCategory: FilterCategory,
  displayName: Map<string, string>,
  hideFilterCategory: FilterCategory[],
  mappedValues?: Map<string, string>,
) => {
  return publicationsFilterOptionToMetadataMap.has(filterCategory) &&
    pubicationsFilterOptionToValuesMap.has(filterCategory)
    ? {
        displayCount: true,
        displayName,
        expanded: true,
        filterCategory,
        label: filterOptionToLabelMap.get(filterCategory),
        metadata: publicationsFilterOptionToMetadataMap.get(filterCategory),
        options: {},
        values: pubicationsFilterOptionToValuesMap.get(filterCategory),
        mappedValues,
        hide: hideFilterCategory.includes(filterCategory),
      }
    : null;
};

export const updateFilterOptionWithValue = (
  filterValue: string | [string],
  filter: Filter,
  filterValuesSet: Set<string>,
) => {
  if (filterValue) {
    if (typeof filterValue === 'string' && !filterValuesSet.has(filterValue)) {
      filterValuesSet.add(filterValue);
      if (!filter.options[filterValue]) {
        filter.options[filterValue] = {
          count: 1,
          label: filter.displayName
            ? filter.displayName.get(filterValue) || filterValue
            : filterValue,
          selected: false,
          value: filterValue,
        };
      } else {
        filter.options[filterValue].count += 1;
      }
    } else if (Array.isArray(filterValue)) {
      const filterValues = (filterValue as string[])?.filter(
        (value) => !filterValuesSet.has(value),
      );
      filterValues.forEach((value) => filterValuesSet.add(value));
      filterValues.forEach((option) => {
        if (!filter.options[option]) {
          filter.options[option] = {
            count: 1,
            label: filter.displayName ? filter.displayName.get(option) || option : option,
            selected: false,
            value: option,
          };
        } else {
          filter.options[option].count += 1;
        }
      });
    }
  }
};

export const getFilterOptionsKeys = (filterOptions: FilterOptions) => {
  let filterOptionsKeys = Object.keys(filterOptions);
  if (filterOptionsKeys.includes(ContentType.Pattern)) {
    if (filterOptionsKeys.includes(FilterCategory.RequireCode)) {
      filterOptionsKeys = filterOptionsKeys.filter(
        (option) => option !== ContentType.Pattern && option !== FilterCategory.RequireCode,
      );
      filterOptionsKeys.unshift(FilterCategory.RequireCode);
      filterOptionsKeys.unshift(ContentType.Pattern);
    }
  }
  return filterOptionsKeys;
};

export const applyFilterToPublication = (
  publication: Publication,
  filtersToApply: FilterOptionToApply[],
) => {
  return filtersToApply.every((filter) => {
    const hasIncludeCodeFilter = filter.options.some(
      (option) => option.label === filterOptionToLabelMap.get(FilterCategory.IncludeCode),
    );
    const hasBRReviewStatusFilter = filter.values.includes('barRaiserReviewStatus');
    return filter.values.some((value) => {
      const filterValue =
        filter.metadata !== ''
          ? // @ts-expect-error TS7053 - strict migration
            publication[filter.metadata][value]
          : filter.mappedValues
            ? // @ts-expect-error TS7053 - strict migration
              filter.mappedValues.get(publication[value])
            : // @ts-expect-error TS7053 - strict migration
              publication[value];
      if (hasIncludeCodeFilter) {
        return publication?.contentTypeMetadata?.codeLinks?.length > 0;
      } else if (hasBRReviewStatusFilter) {
        const brValue = isBRApproved(publication) ? BR_APPROVED_VALUE : BR_APPROVAL_PENDING_VALUE;
        return filter.options.some((option: FilterOption) => option.value === brValue);
      } else if (typeof filterValue === 'string') {
        return (
          filterValue &&
          filter.options.some((option: FilterOption) => {
            return option.value === filterValue;
          })
        );
      } else {
        return (
          filterValue &&
          filter.options.some((option: FilterOption) => filterValue.includes(option.value))
        );
      }
    });
  });
};

// Sorting rule:
// Technologies should be sorted in descending order by number of results
// Other filters sorting by A-Z. Put 'N/A' and 'All other workloads' at last position
export const sortPublicationFilter = (
  filter: Filter,
  filterOptionsKeys: string[],
  filterSectionId: string,
) => {
  const { options } = filter;
  arraySort(filterOptionsKeys, (filterKey1: string, filterKey2: string) => {
    if (filterSectionId === FilterCategory.TechDomains) {
      // @ts-expect-error TS2532 - strict migration
      const count1 = options[filterKey1].count;
      // @ts-expect-error TS2532 - strict migration
      const count2 = options[filterKey2].count;
      return count2 - count1;
    } else {
      const filterOptionLabel1 = options[filterKey1]?.label;
      const filterOptionLabel2 = options[filterKey2]?.label;
      // @ts-expect-error TS18048 - strict migration
      return filterOptionLabel1.localeCompare(filterOptionLabel2);
    }
  });
  const moveToEnd = (lastKey: string) => {
    if (filterOptionsKeys.includes(lastKey)) {
      filterOptionsKeys = filterOptionsKeys.filter((optionKey) => optionKey !== lastKey);
      filterOptionsKeys.push(lastKey);
    }
  };
  switch (filterSectionId) {
    case FilterCategory.RType:
      moveToEnd(RType.NotApplicable);
      break;
    case FilterCategory.Workload:
      moveToEnd(Workload.Other);
      break;
  }
  return filterOptionsKeys;
};
